import React from "react";
import {
    ContentState,
    convertFromHTML,
    convertToRaw,
    Editor,
    EditorState,
    getDefaultKeyBinding,
    RichUtils
} from "draft-js";
import "./style.scss";
import 'draft-js/dist/Draft.css';
import draftToHtml from 'draftjs-to-html';
import i18n from "i18next";
import ColumnTitleWithIcon from "../ColumnTitleWithIcon";

class SectionRichText extends React.Component {
    constructor(props) {
        super(props);
        const sampleMarkup = this.props.value || "";
        const blocksFromHTML = convertFromHTML(sampleMarkup);
        const state = ContentState.createFromBlockArray(
            blocksFromHTML.contentBlocks,
            blocksFromHTML.entityMap,
        );
        this.state = {editorState: EditorState.createWithContent(state)};

        this.focus = () => this.refs.editor.focus();

        this.handleKeyCommand = this._handleKeyCommand.bind(this);
        this.mapKeyToEditorCommand = this._mapKeyToEditorCommand.bind(this);
        this.toggleBlockType = this._toggleBlockType.bind(this);
        this.toggleInlineStyle = this._toggleInlineStyle.bind(this);
    }

    onChange = (editorState) => {
        const {onPropChange, column} = this.props;
        this.setState({editorState});

        const rawContentState = convertToRaw(editorState.getCurrentContent());
        const markup = draftToHtml(rawContentState);

        if(onPropChange) {
            onPropChange({[column.accessor]: markup});
        }
    };

    _handleKeyCommand(command, editorState) {
        const newState = RichUtils.handleKeyCommand(editorState, command);
        if (newState) {
            this.onChange(newState);
            return true;
        }
        return false;
    }

    _mapKeyToEditorCommand(e) {
        e.stopPropagation();
        if (e.keyCode === 9 /* TAB */) {
            const newEditorState = RichUtils.onTab(
                e,
                this.state.editorState,
                4, /* maxDepth */
            );
            if (newEditorState !== this.state.editorState) {
                this.onChange(newEditorState);
            }
            return;
        }
        return getDefaultKeyBinding(e);
    }

    _toggleBlockType(blockType) {
        this.onChange(
            RichUtils.toggleBlockType(
                this.state.editorState,
                blockType
            )
        );
    }

    _toggleInlineStyle(inlineStyle) {
        this.onChange(
            RichUtils.toggleInlineStyle(
                this.state.editorState,
                inlineStyle
            )
        );
    }

    componentDidUpdate(prevProps) {
        if (this.props.value !== prevProps.value) {
            const currentContent = this.state.editorState.getCurrentContent();
            const markup = draftToHtml(convertToRaw(currentContent));

            if (markup !== this.props.value) {
                const sampleMarkup = this.props.value || "";
                const blocksFromHTML = convertFromHTML(sampleMarkup);
                const state = ContentState.createFromBlockArray(
                    blocksFromHTML.contentBlocks,
                    blocksFromHTML.entityMap,
                );
                this.setState({editorState: EditorState.createWithContent(state)});
            }
        }
    }

    render() {
        const {editorState} = this.state;
        const {column, item, onPropChange} = this.props;
        let className = 'RichEditor-editor';
        const contentState = editorState.getCurrentContent();

        if (!contentState.hasText()) {
            if (contentState.getBlockMap().first().getType() !== 'unstyled') {
                className += ' RichEditor-hidePlaceholder';
            }
        }

        return (
            <div className="RichEditor-root sectionItemHtmlEditor">
                <ColumnTitleWithIcon column={column} item={item} onPropChange={onPropChange}/>
                <div className={"sectionItemHtmlEditor-body"}>
                    {column.showExtraEdit && (
                        <BlockStyleControls
                            editorState={editorState}
                            onToggle={this.toggleBlockType}
                        />
                    )}
                    <InlineStyleControls
                        editorState={editorState}
                        onToggle={this.toggleInlineStyle}
                    />
                    <div className={className} onClick={this.focus}>
                        <Editor
                            readOnly={column.isReadonly}
                            blockStyleFn={getBlockStyle}
                            customStyleMap={styleMap}
                            editorState={editorState}
                            handleKeyCommand={this.handleKeyCommand}
                            keyBindingFn={this.mapKeyToEditorCommand}
                            onChange={this.onChange}
                            ref="editor"
                            spellCheck={true}
                        />
                    </div>
                </div>
            </div>
        );
    }
}

const styleMap = {
    CODE: {
        backgroundColor: 'rgba(0, 0, 0, 0.05)',
        fontFamily: '"Inconsolata", "Menlo", "Consolas", monospace',
        fontSize: 16,
        padding: 2,
    },
};

function getBlockStyle(block) {
    switch (block.getType()) {
        case 'blockquote': return 'RichEditor-blockquote';
        default: return null;
    }
}

class StyleButton extends React.Component {
    constructor() {
        super();
        this.onToggle = (e) => {
            e.preventDefault();
            this.props.onToggle(this.props.style);
        };
    }

    render() {
        let className = 'RichEditor-styleButton';
        if (this.props.active) {
            className += ' RichEditor-activeButton';
        }

        return (
            <span className={className} onMouseDown={this.onToggle}>
              {this.props.label}
            </span>
        );
    }
}

const BLOCK_TYPES = [
    // {label: i18n.t("html_editor.H1"), style: 'header-one'},
    // {label: i18n.t("html_editor.H2"), style: 'header-two'},
    // {label: i18n.t("html_editor.H3"), style: 'header-three'},
    // {label: i18n.t("html_editor.H4"), style: 'header-four'},
    // {label: i18n.t("html_editor.H5"), style: 'header-five'},
    // {label: i18n.t("html_editor.H6"), style: 'header-six'},
    // {label: i18n.t("html_editor.BLOCKQUOTE"), style: 'blockquote'},
    // {label: i18n.t("html_editor.UL"), style: 'unordered-list-item'},
    // {label: i18n.t("html_editor.OL"), style: 'ordered-list-item'},
];

const BlockStyleControls = (props) => {
    const {editorState} = props;
    const selection = editorState.getSelection();
    const blockType = editorState
        .getCurrentContent()
        .getBlockForKey(selection.getStartKey())
        .getType();

    return (
        <div className="RichEditor-controls">
            {BLOCK_TYPES.map((type) =>
                <StyleButton
                    key={type.label}
                    active={type.style === blockType}
                    label={type.label}
                    onToggle={props.onToggle}
                    style={type.style}
                />
            )}
        </div>
    );
};

const INLINE_STYLES = [
    {label: i18n.t("html_editor.BOLD"), style: 'BOLD'},
    {label: i18n.t("html_editor.ITALIC"), style: 'ITALIC'},
    {label: i18n.t("html_editor.UNDERLINE"), style: 'UNDERLINE'},
    {label: i18n.t("html_editor.MONOSPACE"), style: 'CODE'},
];

const InlineStyleControls = (props) => {
    const currentStyle = props.editorState.getCurrentInlineStyle();

    return (
        <div className="RichEditor-controls">
            {INLINE_STYLES.map((type) =>
                <StyleButton
                    key={type.label}
                    active={currentStyle.has(type.style)}
                    label={type.label}
                    onToggle={props.onToggle}
                    style={type.style}
                />
            )}
        </div>
    );
};

export default SectionRichText;
